import React, { Fragment } from "react";
import DatePicker from "react-datepicker";
import moment from "moment";
import { preventNegativeValueInDocs } from "_helpers/helpers";
import { SignPad } from "Docs/Components";
import StateTaxNc4PartII from "./StateTaxNc4PartII";
import StateTaxNc4Schedule1 from "./StateTaxNc4Schedule1";
import StateTaxNc4Schedule2 from "./StateTaxNc4Schedule2";

const NC4_PART1_INSTRUCTION = (
  <>
    If you answered &quot;No&quot; to all of the above, <strong>STOP HERE</strong> and
    enter <strong>ZERO (0)</strong> as total allowances on Form NC-4, Line 1. If you
    answered &quot;Yes&quot; to any of the above, you may choose to go to Page 2, Part
    II to determine if you qualify for additional allowances. Otherwise, enter{" "}
    <strong>ZERO (0)</strong> on Form NC-4, Line 1.
  </>
);

const NC4_SURVIVING_SPOUSE_INSTRUCTION = (
  <>
    If you answered &quot;No&quot; to all of the above, <strong>STOP HERE</strong> and
    enter <strong>FIVE (5)</strong> as total allowances on Form NC-4, Line 1. If you
    answered &quot;Yes&quot; to any of the above, you may choose to go to Part II to
    determine if you qualify for additional allowances. Otherwise, enter{" "}
    <strong>FIVE (5)</strong> on Form NC-4, Line 1.
  </>
);

/** Part I worksheet field keys per filing-status block (mutual exclusion). */
const NC4_PART1_WORKSHEET_KEYS = {
  single: [
    "nc4_worksheet_single_1",
    "nc4_worksheet_single_2",
    "nc4_worksheet_single_3",
    "nc4_worksheet_single_4",
  ],
  mfj: [
    "nc4_worksheet_mfj_1",
    "nc4_worksheet_mfj_2",
    "nc4_worksheet_mfj_3",
    "nc4_worksheet_mfj_4",
    "nc4_worksheet_mfj_5",
  ],
  mfs: [
    "nc4_worksheet_mfs_1",
    "nc4_worksheet_mfs_2",
    "nc4_worksheet_mfs_3",
    "nc4_worksheet_mfs_4",
  ],
  hoh: [
    "nc4_worksheet_hoh_1",
    "nc4_worksheet_hoh_2",
    "nc4_worksheet_hoh_3",
    "nc4_worksheet_hoh_4",
  ],
  ss: [
    "nc4_worksheet_ss_1",
    "nc4_worksheet_ss_2",
    "nc4_worksheet_ss_3",
    "nc4_worksheet_ss_4",
  ],
};

function nc4WorksheetSectionHasAnyAnswer(form, fieldKeys) {
  return fieldKeys.some((k) => {
    const val = form[k];
    return (
      val !== undefined &&
      val !== null &&
      String(val).trim() !== ""
    );
  });
}

/** First block (in form order) with any yes/no answered; others are disabled. */
function getNc4Part1ActiveWorksheetSection(form) {
  const order = ["single", "mfj", "mfs", "hoh", "ss"];
  for (const id of order) {
    if (nc4WorksheetSectionHasAnyAnswer(form, NC4_PART1_WORKSHEET_KEYS[id])) {
      return id;
    }
  }
  return null;
}

function Nc4WorksheetYesNo({ form, onFormChange, fieldKey, question, disabled }) {
  const v = form[fieldKey] ?? "";
  const groupName = `nc4_ws_${fieldKey}`;
  const noId = `nc4_ws_${fieldKey}_no`;
  const yesId = `nc4_ws_${fieldKey}_yes`;

  return (
    <div className="row form-row state-tax-nc4-ws-q-row">
      <div className="col">
        <div className="inputs-col">
          <div className="inputs-head">{question}</div>
          <div className="check_radio_group gender_radio" role="radiogroup">
            <div className="check_radio_item agreement_radio">
              <input
                type="radio"
                name={groupName}
                field={`form.${fieldKey}`}
                id={noId}
                value="no"
                checked={v === "no"}
                disabled={disabled}
                onChange={onFormChange}
              />
              <span className="radio_checkbox"></span>
              <label htmlFor={noId}>No</label>
            </div>
            <div className="check_radio_item agreement_radio">
              <input
                type="radio"
                name={groupName}
                field={`form.${fieldKey}`}
                id={yesId}
                value="yes"
                checked={v === "yes"}
                disabled={disabled}
                onChange={onFormChange}
              />
              <span className="radio_checkbox"></span>
              <label htmlFor={yesId}>Yes</label>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Nc4WorksheetSection({
  title,
  form,
  onFormChange,
  questions,
  instruction = NC4_PART1_INSTRUCTION,
  disabled = false,
}) {
  return (
    <section
      className={`state-tax-nc4-ws-section${disabled ? " state-tax-nc4-ws-section--disabled" : ""}`}
    >
      <div className="row form-row state-tax-nc4-ws-section-head-row">
        <div className="col">
          <h4 className="state-tax-nc4-ws-section-title">{title}</h4>
        </div>
      </div>
      {questions.map(({ fieldKey, text }) => (
        <Nc4WorksheetYesNo
          key={fieldKey}
          form={form}
          onFormChange={onFormChange}
          fieldKey={fieldKey}
          question={text}
          disabled={disabled}
        />
      ))}
      <p className="state-tax-nc4-ws-instruction">{instruction}</p>
    </section>
  );
}

/**
 * North Carolina Form NC-4 — page 1: instructions; page 2: certificate;
 * page 3: Part I; page 4: Part II; page 5: Schedule 1; page 6: Schedule 2.
 */
export default function StateTaxAshville({
  form,
  errors,
  sign,
  page,
  onFormChange,
  onSsnChange,
  onSignature,
  onDateChange,
  setSignContainer,
  onSchedule1Skip,
}) {
  if (page === 1) {
    return (
      <div className="row state-tax-nc4-w4">
        <div className="col-md-12">
          <div className="state-tax-nc4-columns">
            <div className="state-tax-nc4-col">
              <section className="state-tax-nc4-block">
                <h4 className="state-tax-nc4-heading">Purpose</h4>
                <p>
                  Complete Form NC-4 so your employer can withhold the correct
                  amount of state income tax from your pay. If you do not submit
                  an NC-4, your employer must withhold as if you are single with
                  zero withholding allowances.
                </p>
              </section>

              <section className="state-tax-nc4-block">
                <h4 className="state-tax-nc4-heading">Form NC-4 EZ</h4>
                <p>
                  You may use Form NC-4 EZ if you intend to claim the same
                  allowances for state purposes as you claim for federal
                  purposes on your Form W-4, you do not wish to claim
                  allowances for itemized deductions, and you have a nonworking
                  spouse or no spouse.
                </p>
              </section>

              <section className="state-tax-nc4-block">
                <h4 className="state-tax-nc4-heading">Form NC-4 NRA</h4>
                <p>
                  Nonresident aliens must use Form NC-4 NRA and follow the
                  instructions for nonresident aliens in the NC-4 instructions.
                </p>
              </section>

              <section className="state-tax-nc4-block">
                <h4 className="state-tax-nc4-heading">
                  Form NC-4 general instructions
                </h4>
                <p>
                  Use the Allowance Worksheet in the instructions to determine
                  the correct number of allowances, unless an exception applies.
                  If you claim more allowances than you are entitled to, you
                  may owe additional tax and interest.
                </p>
              </section>

              <section className="state-tax-nc4-block">
                <h4 className="state-tax-nc4-heading">Two or more jobs</h4>
                <p>
                  If you have more than one job, or you and your spouse both
                  work, read the instructions carefully. You may need to divide
                  allowances between Forms NC-4 or adjust withholding to avoid
                  having too little tax withheld.
                </p>
              </section>

              <section className="state-tax-nc4-block">
                <h4 className="state-tax-nc4-heading">Nonwage income</h4>
                <p>
                  If you have significant nonwage income (such as interest,
                  dividends, or self-employment income), you may need to make
                  estimated income tax payments or request additional
                  withholding so you do not owe tax when you file.
                </p>
              </section>
            </div>

            <div className="state-tax-nc4-col">
              <section className="state-tax-nc4-block">
                <p>
                  You may need to file Form NC-40, Estimated Income Tax, and
                  pay estimated tax. For more information, visit{" "}
                  <a
                    href="https://www.ncdor.gov"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    ncdor.gov
                  </a>
                  .
                </p>
              </section>

              <section className="state-tax-nc4-block">
                <h4 className="state-tax-nc4-heading">Head of household</h4>
                <p>
                  You may claim head of household status only if you are
                  unmarried and pay more than half the cost of keeping up a home
                  for yourself and a qualifying person. Do not claim head of
                  household if you are married and your spouse lived with you
                  during the last six months of the year (with exceptions
                  described in the instructions).
                </p>
              </section>

              <section className="state-tax-nc4-block">
                <h4 className="state-tax-nc4-heading">Surviving spouse</h4>
                <p>
                  You may be able to use surviving spouse status for the two
                  years after your spouse&apos;s death if you meet all of these
                  conditions:
                </p>
                <ol className="state-tax-nc4-ol">
                  <li>
                    For the year your spouse died, you were eligible to file a
                    joint return, you did not remarry before the end of the tax
                    year, and you pay more than half the cost of keeping up a
                    home that was the main home of a qualifying dependent for the
                    whole year.
                  </li>
                  <li>
                    You could have filed a joint return with your spouse for the
                    year of death (whether or not you actually filed jointly).
                  </li>
                </ol>
              </section>

              <section className="state-tax-nc4-block">
                <h4 className="state-tax-nc4-heading">Married taxpayers</h4>
                <p>
                  If you are married, complete the worksheet using the filing
                  status that matches how you will file your North Carolina
                  return:
                </p>
                <ul className="state-tax-nc4-ul">
                  <li>
                    <strong>Married filing jointly:</strong> You may combine
                    income and deductions and complete one worksheet as if you
                    had one job, or you may complete separate worksheets if you
                    prefer to keep withholding separate.
                  </li>
                  <li>
                    <strong>Married filing separately:</strong> Use separate
                    worksheets. You generally cannot claim allowances your spouse
                    already claims on another NC-4.
                  </li>
                </ul>
              </section>
            </div>
          </div>

          <div className="disclaimers" role="note">
            <p>
              <strong>Caution:</strong> All NC-4 forms are subject to review by
              the North Carolina Department of Revenue. If you furnish false
              information that results in less income tax withheld than is
              required, you may be subject to a penalty of 50% of the
              difference between the amount required to be withheld and the
              amount actually withheld, in addition to any tax, interest, and
              other penalties.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (page === 3) {
    const nc4Part1ActiveWs = getNc4Part1ActiveWorksheetSection(form);

    return (
      <div className="row state-tax-nc4-w4 state-tax-nc4-ws">
        <div className="col-md-12">
          <h3 className="state-tax-nc4-ws-page-title mt-0">Part I — Allowances</h3>
          <p className="state-tax-nc4-ws-intro">
            Answer all of the following questions for your filing status.
          </p>
          <hr className="state-tax-nc4-ws-divider" />

          <Nc4WorksheetSection
            title="Single"
            form={form}
            onFormChange={onFormChange}
            disabled={nc4Part1ActiveWs !== null && nc4Part1ActiveWs !== "single"}
            questions={[
              {
                fieldKey: "nc4_worksheet_single_1",
                text: "Will your N.C. itemized deductions from Page 3, Schedule 1 exceed $15,249?",
              },
              {
                fieldKey: "nc4_worksheet_single_2",
                text: "Will your N.C. child deduction amount from Page 3, Schedule 2 exceed $2,499?",
              },
              {
                fieldKey: "nc4_worksheet_single_3",
                text: "Will you have federal adjustments or N.C. deductions from income?",
              },
              {
                fieldKey: "nc4_worksheet_single_4",
                text: "Will you be able to claim any N.C. tax credits or tax credit carryovers?",
              },
            ]}
          />
          <hr className="state-tax-nc4-ws-divider" />

          <Nc4WorksheetSection
            title="Married filing jointly"
            form={form}
            onFormChange={onFormChange}
            disabled={nc4Part1ActiveWs !== null && nc4Part1ActiveWs !== "mfj"}
            questions={[
              {
                fieldKey: "nc4_worksheet_mfj_1",
                text: "Will your N.C. itemized deductions from Page 3, Schedule 1 exceed $27,999?",
              },
              {
                fieldKey: "nc4_worksheet_mfj_2",
                text: "Will your N.C. child deduction amount from Page 3, Schedule 2 exceed $2,499?",
              },
              {
                fieldKey: "nc4_worksheet_mfj_3",
                text: "Will you have federal adjustments or N.C. deductions from income?",
              },
              {
                fieldKey: "nc4_worksheet_mfj_4",
                text: "Will you be able to claim any N.C. tax credits or tax credit carryovers?",
              },
              {
                fieldKey: "nc4_worksheet_mfj_5",
                text: "Will your spouse receive combined wages and taxable retirement benefits of less than $10,250 or only retirement benefits not subject to N.C. income tax?",
              },
            ]}
          />
          <hr className="state-tax-nc4-ws-divider" />

          <Nc4WorksheetSection
            title="Married filing separately"
            form={form}
            onFormChange={onFormChange}
            disabled={nc4Part1ActiveWs !== null && nc4Part1ActiveWs !== "mfs"}
            questions={[
              {
                fieldKey: "nc4_worksheet_mfs_1",
                text: "Will your portion of N.C. itemized deductions from Page 3, Schedule 1 exceed $15,249?",
              },
              {
                fieldKey: "nc4_worksheet_mfs_2",
                text: "Will your N.C. child deduction amount from Page 3, Schedule 2 exceed $2,499?",
              },
              {
                fieldKey: "nc4_worksheet_mfs_3",
                text: "Will you have federal adjustments or N.C. deductions from income?",
              },
              {
                fieldKey: "nc4_worksheet_mfs_4",
                text: "Will you be able to claim any N.C. tax credits or tax credit carryovers?",
              },
            ]}
          />
          <hr className="state-tax-nc4-ws-divider" />

          <Nc4WorksheetSection
            title="Head of household"
            form={form}
            onFormChange={onFormChange}
            disabled={nc4Part1ActiveWs !== null && nc4Part1ActiveWs !== "hoh"}
            questions={[
              {
                fieldKey: "nc4_worksheet_hoh_1",
                text: "Will your N.C. itemized deductions from Page 3, Schedule 1 exceed $21,624?",
              },
              {
                fieldKey: "nc4_worksheet_hoh_2",
                text: "Will your N.C. child deduction amount from Page 3, Schedule 2 exceed $2,499?",
              },
              {
                fieldKey: "nc4_worksheet_hoh_3",
                text: "Will you have federal adjustments or N.C. deductions from income?",
              },
              {
                fieldKey: "nc4_worksheet_hoh_4",
                text: "Will you be able to claim any N.C. tax credits or tax credit carryovers?",
              },
            ]}
          />
          <hr className="state-tax-nc4-ws-divider" />

          <Nc4WorksheetSection
            title="Surviving spouse"
            form={form}
            onFormChange={onFormChange}
            instruction={NC4_SURVIVING_SPOUSE_INSTRUCTION}
            disabled={nc4Part1ActiveWs !== null && nc4Part1ActiveWs !== "ss"}
            questions={[
              {
                fieldKey: "nc4_worksheet_ss_1",
                text: "1. Will your N.C. itemized deductions from Page 3, Schedule 1 exceed $27,999?",
              },
              {
                fieldKey: "nc4_worksheet_ss_2",
                text: "2. Will your N.C. child deduction amount from Page 3, Schedule 2 exceed $2,499?",
              },
              {
                fieldKey: "nc4_worksheet_ss_3",
                text: "3. Will you have federal adjustments or N.C. deductions from income?",
              },
              {
                fieldKey: "nc4_worksheet_ss_4",
                text: "4. Will you be able to claim any N.C. tax credits or tax credit carryovers?",
              },
            ]}
          />

          <hr className="state-tax-nc4-ws-divider" />
        </div>
      </div>
    );
  }

  if (page === 4) {
    return (
      <StateTaxNc4PartII
        form={form}
        errors={errors}
        onFormChange={onFormChange}
      />
    );
  }

  if (page === 5) {
    return (
      <StateTaxNc4Schedule1
        form={form}
        errors={errors}
        onFormChange={onFormChange}
        onSchedule1Skip={onSchedule1Skip}
      />
    );
  }

  if (page === 6) {
    return (
      <StateTaxNc4Schedule2
        form={form}
        errors={errors}
        onFormChange={onFormChange}
      />
    );
  }

  const ncFilingStatusSelected =
    !!form.filing_status_single ||
    !!form.filing_status_married ||
    !!form.filing_status_married_separate ||
    !!form.filing_status_head_of_household;

  return (
    <div className="row state-tax-nc4-w4 state-tax-nc4-cert">
      <div className="col-md-12">
        <h3 className="state-tax-nc4-cert-title mt-0">Form NC-4 certificate</h3>
        <p className="state-tax-nc4-cert-lead">
          Complete the certificate below. Use the instructions on the previous
          page if you need help determining your allowances.
        </p>

        <div className="state-tax-section">
          <div className="state-tax-boise-stack-field">
            <div className="state-tax-boise-stack-field-label">
              <span>1. Total number of allowances you are claiming</span>
            </div>
            <div className="state-tax-boise-stack-field-input">
              <input
                type="number"
                placeholder="- Enter here -"
                className={`${
                  errors["form.allowances"] ? "error" : ""
                } price_input`}
                name="allowances"
                id="nc4_allowances"
                value={form.allowances ?? ""}
                field="form.allowances"
                onChange={onFormChange}
                onWheel={(e) => e.target.blur()}
                onKeyDown={preventNegativeValueInDocs}
              />
              {errors["form.allowances"] && (
                <label htmlFor="nc4_allowances" className="error_label">
                  {errors["form.allowances"]}
                </label>
              )}
            </div>
          </div>

          <div className="state-tax-boise-stack-field">
            <div className="state-tax-boise-stack-field-label">
              <span>
                2. Additional amount, if any, you want withheld from each pay
                period (enter whole dollars)
              </span>
            </div>
            <div className="state-tax-boise-stack-field-input">
              <input
                type="number"
                placeholder="- Enter here -"
                className={`${
                  errors["form.additional_amount"] ? "error" : ""
                } price_input`}
                name="additional_amount"
                id="nc4_additional_amount"
                value={form.additional_amount ?? ""}
                field="form.additional_amount"
                onChange={onFormChange}
                onWheel={(e) => e.target.blur()}
                onKeyDown={preventNegativeValueInDocs}
              />
              {errors["form.additional_amount"] && (
                <label
                  htmlFor="nc4_additional_amount"
                  className="error_label"
                >
                  {errors["form.additional_amount"]}
                </label>
              )}
            </div>
          </div>
        </div>

        <div className="state-tax-section">
          <div className="form-group">
            <span>Social Security number (SSN)</span>
            <input
              type="text"
              className="form-control"
              value={form.ssn || ""}
              onChange={onSsnChange}
              placeholder="XXX-XX-XXXX"
              maxLength={11}
            />
          </div>
        </div>

        <div className="state-tax-section">
          <div className="state-tax-boise-withholding-prompt">
            <strong>FILING STATUS</strong> (select one)
          </div>
          <div className="form-group state-tax-select-one">
            <span>3. Select one:</span>
            <div className="state-tax-checkbox-group">
              <span className="state-tax-checkbox-label">
                <input
                  type="checkbox"
                  field="form.filing_status_single"
                  checked={!!form.filing_status_single}
                  disabled={
                    ncFilingStatusSelected && !form.filing_status_single
                  }
                  onChange={onFormChange}
                />
                Single
              </span>
              <span className="state-tax-checkbox-label">
                <input
                  type="checkbox"
                  field="form.filing_status_married"
                  checked={!!form.filing_status_married}
                  disabled={
                    ncFilingStatusSelected && !form.filing_status_married
                  }
                  onChange={onFormChange}
                />
                Married filing jointly
              </span>
              <span className="state-tax-checkbox-label">
                <input
                  type="checkbox"
                  field="form.filing_status_married_separate"
                  checked={!!form.filing_status_married_separate}
                  disabled={
                    ncFilingStatusSelected &&
                    !form.filing_status_married_separate
                  }
                  onChange={onFormChange}
                />
                Married filing separately
              </span>
              <span className="state-tax-checkbox-label">
                <input
                  type="checkbox"
                  field="form.filing_status_head_of_household"
                  checked={!!form.filing_status_head_of_household}
                  disabled={
                    ncFilingStatusSelected &&
                    !form.filing_status_head_of_household
                  }
                  onChange={onFormChange}
                />
                Head of household
              </span>
            </div>
          </div>
        </div>

        <div className="state-tax-section">
          <div className="form-group">
            <span>Mailing address</span>
            <input
              type="text"
              className="form-control"
              field="form.address"
              value={form.address || ""}
              onChange={onFormChange}
              placeholder="Street address"
            />
          </div>
          <div className="form-row state-tax-address-row">
            <div className="form-group">
              <span>City</span>
              <input
                type="text"
                className="form-control"
                field="form.city"
                value={form.city || ""}
                onChange={onFormChange}
                placeholder="City"
              />
            </div>
            <div className="form-group">
              <span>State / Province</span>
              <input
                type="text"
                className="form-control"
                field="form.state"
                value={form.state || ""}
                onChange={onFormChange}
                placeholder="State"
              />
            </div>
            <div className="form-group">
              <span>ZIP / Postal code</span>
              <input
                type="text"
                className="form-control"
                field="form.zip_code"
                value={form.zip_code || ""}
                onChange={onFormChange}
                placeholder="ZIP"
              />
            </div>
          </div>
          <div className="form-group">
            <span>Country</span>
            <input
              type="text"
              className="form-control"
              field="form.address_country"
              value={form.address_country || ""}
              onChange={onFormChange}
              placeholder="United States"
            />
            <span className="state-tax-nc4-country-hint">
              If your mailing address is outside the U.S., enter the country.
            </span>
          </div>
        </div>

        <div className="state-tax-section">
          <div className="form-row state-tax-name-row">
            <div className="form-group state-tax-first-name flex-1">
              <span>First name</span>
              <input
                type="text"
                className="form-control"
                field="form.first_name"
                value={form.first_name || ""}
                onChange={onFormChange}
                placeholder="First name"
              />
            </div>
            <div className="form-group state-tax-initial flex-1">
              <span>M.I.</span>
              <input
                type="text"
                className="form-control"
                field="form.middle_initial"
                value={form.middle_initial || ""}
                onChange={onFormChange}
                placeholder=""
                maxLength={2}
              />
            </div>
            <div className="form-group state-tax-last-name flex-1">
              <span>Last name</span>
              <input
                type="text"
                className="form-control"
                field="form.last_name"
                value={form.last_name || ""}
                onChange={onFormChange}
                placeholder="Last name"
              />
            </div>
          </div>
        </div>

        <p className="disclaimers">
          <strong>Note:</strong> Your eligibility to claim allowances may be
          subject to review by the North Carolina Department of Revenue.
        </p>

        <div className="row form-row">
          <div className="col">
            <div className="inputs-col">
              <div className="inputs-head">
                Employee signature
                <span className="required-star">&#x2A;</span>
              </div>
              <div className="inputs-items">
                <div ref={setSignContainer} className="sign_here">
                  {sign && (
                    <Fragment>
                      <SignPad
                        id="stateTaxNc4SignPad"
                        height={sign.height || 144}
                        width={sign.width || 446}
                        handleSignature={onSignature}
                        signature={form.employee_signature}
                      />
                      <input
                        type="hidden"
                        value={form.employee_signature || ""}
                        name="employee_signature"
                        field="form.employee_signature"
                        required
                      />
                      {errors["form.employee_signature"] && (
                        <label
                          htmlFor="employee_signature"
                          className="error_label"
                        >
                          {errors["form.employee_signature"]}
                        </label>
                      )}
                    </Fragment>
                  )}
                </div>
              </div>
            </div>
            <div className="inputs-col">
              <div className="inputs-head">
                Date<span className="required-star">&#x2A;</span>
              </div>
              <div className="inputs-items">
                <DatePicker
                  className={`${errors["form.signed_date"] ? "error" : ""} w-100`}
                  name="signed_date"
                  id="signed_date_nc4"
                  required
                  selected={moment(form.signed_date)}
                  field="form.signed_date"
                  placeholder="mm/dd/yyyy"
                  onChange={(date) => onDateChange("form.signed_date", date)}
                  showMonthDropdown
                  showYearDropdown
                  dropdownMode="select"
                  autoComplete="off"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
